import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store';
import { FormModal } from 'src/components/Modals';
import {
  useChannelForm,
  ChannelType,
} from 'src/components/Channels/useChannelForm';
import { useGetChannelModalTexts } from 'src/components/Channels/useGetChannelModalTexts';
import { CreateButtonProps } from 'src/components/UI/Buttons/CreateButton';
import { ChannelsPageLayoutHeaderContext } from 'src/components/Layout/ChannelsPageLayout';
import { SecondarySidebar } from 'src/components/UI/SecondarySidebar';
import { CreateActionModalProps } from 'src/components/Channels/BaseChannelSidebar';
import { FlagsContext, RouteContext } from 'src/context';
import { SideDrawer } from '../UI/SideDrawer';
import { Hidden } from '@material-ui/core';

const CREATE_CHANNEL_FALLBACK_TITLE = 'Create a new channel';

export interface ChannelsSidebarProps {
  entity: ChannelType;
  onCreate: (values: any) => void;
  initialCreateFormValue: any;
  modalTitle?: string;
  hasChannels?: boolean;
  newButtonProps?: Partial<CreateButtonProps>;
  newChannelAction?: string;
  onSecondaryOptionClick: (option: string) => void;
  noCreateAction?: boolean;
  CreateModal?: React.FC<CreateActionModalProps>;
}

const ChannelsSidebar: React.FC<ChannelsSidebarProps> = ({
  onCreate,
  entity,
  children,
  initialCreateFormValue,
  modalTitle,
  hasChannels = true,
  newButtonProps,
  newChannelAction,
  onSecondaryOptionClick,
  noCreateAction = false,
  CreateModal,
}) => {
  const { EnableChannelsLayoutRedesign } = useContext(FlagsContext);
  const { query } = useContext(RouteContext);
  const { channelId: activeChannelId } = query as { channelId?: string };
  const isClient = useSelector((state: RootState) => state.user.isClient);
  const searchValue = useSelector((state: RootState) => state.ui.searchValue);
  const moduleSettings = useSelector(
    (state: RootState) => state.settings.moduleSettings,
  );
  const [openCreateModal, setOpenCreateModal] = React.useState(false);

  const handleOpenModal = () => setOpenCreateModal(true);
  const handleCloseModal = () => setOpenCreateModal(false);
  const { positiveAction } = useGetChannelModalTexts(entity, moduleSettings);
  const {
    setCreateButtonProps,
    setChannelsListToolbarHidden,
  } = React.useContext(ChannelsPageLayoutHeaderContext);
  // According to the entity type return a specific
  // form for the modal

  /**
   * Based on the action that is selected in menu
   * we should perform new channel action to open modal
   * or call prop function to handle secondary option
   * @param optionValue menu option selected
   */
  const handleClickOptions = (optionValue: string) => {
    if (optionValue === newChannelAction) {
      handleOpenModal();
    } else {
      onSecondaryOptionClick(optionValue);
    }
  };

  const createButtonProps = newButtonProps
    ? {
        onClickOption: handleClickOptions,
        ...newButtonProps,
      }
    : {
        onClick: handleOpenModal,
      };

  React.useEffect(() => {
    // if there are channels, show the channels list toolbar
    if (hasChannels) {
      setChannelsListToolbarHidden(false);
    }

    // if it is an internal user and create action is allowed
    // set the create button props
    if (!isClient && !noCreateAction) {
      setCreateButtonProps(createButtonProps);
    }

    return () => {
      // when channels sidebar unmounts, reset the create button props
      // and hide the channels list toolbar.
      // Also check if search term is empty to avoid hiding the toolbar
      // when user is searching for a channel.
      if (!searchValue) {
        setCreateButtonProps(null);
        setChannelsListToolbarHidden(true);
      }
    };
  }, [hasChannels, noCreateAction, searchValue]);

  if (EnableChannelsLayoutRedesign) {
    return (
      // on mobile the sidebar is hidden when a channel is selected
      <Hidden xsDown={Boolean(activeChannelId)}>
        <SideDrawer>{children}</SideDrawer>
      </Hidden>
    );
  }

  return (
    <SecondarySidebar hidden={!hasChannels}>
      {children}
      {/* If we have passed the component to render the create modal use it
       else fall back to the default create modal  */}
      {CreateModal ? (
        <CreateModal onClose={handleCloseModal} open={openCreateModal} />
      ) : (
        <FormModal
          buttonLabel={positiveAction}
          initialFormValue={initialCreateFormValue}
          entityName="channel"
          handleSave={onCreate}
          useFormHook={() => useChannelForm(entity)}
          open={openCreateModal}
          onClose={handleCloseModal}
          createButtonProps={{ fullWidth: true }}
          title={modalTitle || CREATE_CHANNEL_FALLBACK_TITLE}
        />
      )}
    </SecondarySidebar>
  );
};

export default React.memo(ChannelsSidebar);
